<!--
  FrameX selection bar: bulk Scope/Qty edit + Delete for the current frame
  selection; shown only while frames are selected. Scope/Qty commit directly to
  the group on finish (see wiki: framex, selection bar); Delete dispatches
  `delete-frames` up to Document.svelte's confirm+remove handler.
-->
<script>
  import { createEventDispatcher, onDestroy } from "svelte";

  import { Modal } from "svelte-utilities";

  import { plural } from "../labels.js";
  import TextInput from "#lib/sidebar/TextInput.svelte";

  /** The current frame selection (Document.svelte's `selectedLocationList`). */
  export let selectedLocationList = [];
  /** The group store, to read the selected frames' current scope/qty. */
  export let group;

  const dispatch = createEventDispatcher();

  let scopeValue = "";
  let qtyValue = "";

  // The record ids the fields currently edit — captured from the selection and
  // held across typing, so a commit lands on the frames you were editing even
  // after the selection moves. `committed*` is the baseline last written, so an
  // untouched value never re-commits (no empty undo step, and an untouched
  // "Mixed" multi-select is left alone).
  let editIds = [];
  let committedScope = "";
  let committedQty = "";

  $: count = selectedLocationList.length;

  // The selected frames' current scope/qty. When the selection agrees on one
  // value, that value pre-fills the editable field so it can be edited in
  // place (dark input text, distinct from the grey label). A disagreement
  // leaves the field empty and shows "Mixed" as a placeholder hint.
  $: items = selectedLocationList.map((loc) => $group?.items?.[loc.record_id]).filter(Boolean);
  $: scopes = [...new Set(items.map((i) => i.scope || ""))];
  $: sharedScope = scopes.length === 1 ? scopes[0] : "";
  $: scopePlaceholder = scopes.length > 1 ? "Mixed" : "";
  $: qtys = [...new Set(items.map((i) => i.quantity ?? 0))];
  $: sharedQty = qtys.length === 1 ? String(qtys[0] ?? 0) : "";
  $: qtyPlaceholder = qtys.length > 1 ? "Mixed" : "";

  // Commit the typed Scope/Qty to the frames we were editing (editIds), using
  // the same group.update shape Document.svelte's set-scope/set-qty handlers
  // use — but targeting the captured ids so an edit lands even after the
  // selection moves. Guarded against the committed baseline so an untouched
  // value never writes. One commit per finished edit = one undo step. FrameX
  // frames are collections, whose record type in group.update terms is "item".
  function commitScope() {
    if (scopeValue === committedScope || !editIds.length) return;
    group.update(editIds.map((id) => ({ type: "item", id, diff: { scope: scopeValue } })));
    committedScope = scopeValue;
  }

  function commitQty() {
    if (qtyValue === committedQty || !editIds.length) return;
    const qty = Number(qtyValue);
    if (Number.isNaN(qty)) return;
    group.update(editIds.map((id) => ({ type: "item", id, diff: { quantity: qty } })));
    committedQty = String(qty);
  }

  // On a selection change: flush any pending edit to the frames we WERE editing
  // (editIds still holds them), then retarget to the new selection and seed the
  // fields + committed baseline from its current values. Guarded on the
  // selection identity so live edits (which change sharedScope/sharedQty) don't
  // re-run this and clobber typing mid-edit.
  let prevSelectionKey = "";
  $: selectionKey = selectedLocationList.map((l) => l.id).join(",");
  $: if (selectionKey !== prevSelectionKey) {
    commitScope();
    commitQty();
    prevSelectionKey = selectionKey;
    editIds = [...new Set(selectedLocationList.map((l) => l.record_id))];
    scopeValue = sharedScope;
    qtyValue = sharedQty;
    committedScope = sharedScope;
    committedQty = sharedQty;
  }

  // Backstop: if the bar unmounts (e.g. the selection is cleared) with an
  // uncommitted edit, still save it.
  onDestroy(() => {
    commitScope();
    commitQty();
  });

  // Delete removes whole frames (records), so count distinct frames — a
  // multi-placement frame selected once still deletes as one frame.
  $: frameCount = new Set(selectedLocationList.map((l) => l.record_id)).size;

  let deleteModal;

  function handleDelete() {
    deleteModal.open();
  }

  function confirmDelete() {
    dispatch("delete-frames");
  }
</script>

{#if count > 0}
  <div class="fx-selection-bar">
    <div class="fx-selection-count">{count} frame{plural(count)} selected</div>
    <div class="fx-selection-fields">
      <div class="fx-selection-field">
        <TextInput
          border
          label="Scope"
          inputOnKeydown
          bind:value={scopeValue}
          placeholder={scopePlaceholder}
          on:blur={commitScope}
          on:enter={commitScope} />
      </div>
      <div class="fx-selection-field fx-selection-field-qty">
        <TextInput
          border
          label="Qty"
          inputOnKeydown
          bind:value={qtyValue}
          placeholder={qtyPlaceholder}
          on:blur={commitQty}
          on:enter={commitQty} />
      </div>
      <button type="button" class="fx-selection-delete" on:click={handleDelete}>Delete</button>
    </div>
  </div>
{/if}

<Modal
  bind:this={deleteModal}
  closeable
  closeOnOutclick
  on:confirm={confirmDelete}
  buttons={[
    { label: "Cancel", type: "cancel" },
    { label: "Delete", type: "confirm", style: "danger" },
  ]}>
  <div slot="title">Delete {frameCount} frame{plural(frameCount)}?</div>
  <div slot="content" class="text-sm space-y-2">
    <p>
      This removes {frameCount === 1 ? "the frame" : "these frames"} from the job, including
      {frameCount === 1 ? "its placement" : "their placements"} on every sheet. You can undo this.
    </p>
  </div>
</Modal>

<style lang="scss">
  .fx-selection-bar {
    flex: none;
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 8px 16px;
    border-top: 1px solid var(--fx-border-light, #eceef1);
    background: #fff;
  }

  .fx-selection-count {
    font-size: 12px;
    font-weight: 700;
    color: var(--fx-text, #1a1d21);
  }

  .fx-selection-fields {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .fx-selection-field {
    flex: 1 1 auto;
    min-width: 0;
  }

  .fx-selection-field-qty {
    flex: 0 1 72px;
  }

  .fx-selection-delete {
    all: unset;
    flex: none;
    cursor: pointer;
    font-size: 12px;
    font-weight: 700;
    color: #fff;
    background: var(--fx-danger, #dc2626);
    padding: 7px 10px;
    border-radius: 6px;

    &:hover {
      opacity: 0.9;
    }
  }
</style>
